<script lang="ts" setup>
import WidgetPanels from "@/components/widget/WidgetPanels.vue";
import CommonFilter from "@/components/common/CommonFilter.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import router from "@/router";
import {useChatStore} from "@/features/chat/chat.store";
import {onMounted, onUnmounted, ref, watch} from "vue";
import {useAppStore} from "@/store/app.store";
import {IChat} from "@/features/chat/chat.types";
import CommonPagination from "@/components/common/CommonPagination.vue";
import {IPaginationMeta} from "@/features/pagination/pagination.types";
import {usePersonalStore} from "@/features/personal/personal.store";

const chatStore = useChatStore();
const appStore = useAppStore();
const personalStore = usePersonalStore();

const loading = ref<boolean>(false);
const list = ref<IChat>();
const isFetching = ref<boolean>(false)
const paginationData = ref<IPaginationMeta>()

const handleRowClicked = (row: any, rowIndex: number) => {
  router.push({
    path: `/chat/${row.chat_id}`, query: {
      chat: row.account_id,
      page: paginationData.value?.page,
      page_size: paginationData.value?.page_size,
      prev_page: paginationData.value?.prev_page,
      next_page: paginationData.value?.next_page,
    }
  });
}

const getChats = async () => {
  if (isFetching.value) return

  try {
    isFetching.value = true
    list.value = await chatStore.getChats(loading)
    if (list.value) {
      paginationData.value = {
        has_more: list.value.has_more,
        next_page: list.value.next_page,
        page_size: list.value.page_size,
        page: list.value.page,
        pages: list.value.pages,
        prev_page: list.value.prev_page,
        total: list.value.total,
      }
    }

  } finally {
    isFetching.value = false
  }
}

const handlePageChange = (page: number) => {
  chatStore.commonFilter.page = page;
}

const handleLimitChange = (limit: number) => {
  chatStore.commonFilter.page = 1;
  chatStore.commonFilter.page_size = limit;
}

watch(
    [
      () => appStore.refreshKey,
      () => JSON.stringify(chatStore.commonFilter)
    ],
    () => {
      getChats()
    },
    {immediate: true}
)
</script>

<template>
  <div class="v-chat">
    <WidgetPanels type="chats" :filter="chatStore.commonFilter"/>

    <div class="body">
      <CommonFilter
          :with-shift="false"
          :with-date="false"
          :with-outgoing="false"
          :loading="loading"
          :store="chatStore"
      />

      <CommonTable
          :items="list?.items"
          :loading="loading"
          :headers="chatStore.headers"
          @row-clicked="handleRowClicked"
      >
        <template #id="{index}">
          {{ (list.page - 1) * list.page_size + index + 1 }}
        </template>

        <template #all="{item}">
          {{ item.messages.total }}
        </template>

        <template #text="{item}">
          {{ item.messages.text }}
        </template>

        <template #voice="{item}">
          {{ item.messages.voice + item.messages.document + item.messages.audio }}
        </template>

        <template #call="{item}">
          {{ item.messages.calls }}
        </template>

        <template #photo="{item}">
          {{ item.messages.photo + item.messages.video + item.messages.round }}
        </template>

        <template #all_errors="{item}">
          {{ item.errors.total }}
        </template>

        <template #reply_time="{item}">
          {{ item.errors.reply_time_exceed }}
        </template>

        <template #unanswered="{item}">
          {{ item.errors.missed_calls + item.errors.declined_calls }}
        </template>

        <template #deleted="{item}">
          {{ item.errors.deleted }}
        </template>

        <template #edited="{item}">
          {{ item.errors.edited }}
        </template>
      </CommonTable>
    </div>
    <CommonPagination
        v-if="list && list.items && list.items.length"
        :meta="paginationData"
        @change-page="handlePageChange"
        @change-limit="handleLimitChange"
    />
  </div>
</template>

<style lang="scss" scoped>
.v-chat {
  .body {
    padding: 1rem 1rem 0 1rem;
    height: calc(100vh - 267px);
  }
}
</style>
